import React, { useMemo, useState } from 'react';

import { isAfter, isBefore, isEqual, parseISO } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';

import { Permissions } from 'src/app_deprecated/constants/PermissionsConstants';

import { Button } from 'src/app/components/lib/button';
import {
  ControlBar,
  ControlGroup,
  ControlSearchBox,
  ControlSpacer,
  RefreshButton,
} from 'src/app/components/lib/control-bar';
import { DateRangePicker } from 'src/app/components/lib/date-range-picker';
import { HeaderContainer, HeaderContainerButtonsWrapper } from 'src/app/components/lib/header-container';
import { Heading } from 'src/app/components/lib/heading';
import { MultiFilterSelect } from 'src/app/components/lib/multi-filter-select';
import { Page, PageVariant } from 'src/app/components/lib/page';
import { FixedHeightDataGrid } from 'src/app/components/lib/table';
import { useGetWorkflowCampaignsQuery } from 'src/app/queries/marketing/get-workflow-campaigns';
import { useGetMarketingTagsQuery } from 'src/app/queries/marketing/marketing-tags/get-marketing-tags';
import { useGetSegmentsQuery } from 'src/app/queries/segment/get-segments';
import { usePermissionCheck } from 'src/app/state/user-permissions';

import { useBulkActions } from '../../campaigns/list/shared-actions/bulk-actions';
import { useMarketingCampaignsSharedActionModals } from '../../campaigns/list/shared-actions/marketing-campaigns-shared-action-modals';
import { SharedActionModals } from '../../campaigns/list/shared-actions/shared-action-modals';
import { AutomatedCampaignStatus, CampaignStatus } from '../../campaigns/marketing-campaign-builder/builder-context';
import { WorkflowCampaignBuilder } from '../workflow-campaign-builder/wizard';

import { useColumns } from './use-columns';

import type { DateRange } from '@mui/x-date-pickers-pro';
import type { NonEmptyDateRange } from '@mui/x-date-pickers-pro/internals/models';
import type { DropdownMenuOption } from 'src/app/components/lib/dropdown';

// constants
const statusFilterOptions = [
  { id: CampaignStatus.DRAFT, label: 'Draft' },
  { id: AutomatedCampaignStatus.ACTIVE, label: 'Active' },
  { id: AutomatedCampaignStatus.INACTIVE, label: 'Inactive' },
];

export const WORKFLOW_WIZARD_PATHS = {
  EDIT_WORKFLOW: '/marketing/workflows/edit',
  CREATE_WORKFLOW: '/marketing/workflows/create',
};

const channelFilterOptions = [
  { id: 'email', label: 'Email' },
  { id: 'sms', label: 'SMS' },
  { id: 'push', label: 'Push' },
];

export function WorkflowList() {
  const navigate = useNavigate();
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<DropdownMenuOption[]>([]);
  const [tagFilter, setTagFilter] = useState<DropdownMenuOption[]>([]);
  const [segmentFilter, setSegmentFilter] = useState<DropdownMenuOption[]>([]);
  const [channelFilter, setChannelFilter] = useState<DropdownMenuOption[]>([]);
  const [dateRange, setDateRange] = useState<DateRange<Date>>([null, null]);

  const params = useParams()?.['*'];
  const drawerOpen = useMemo(() => {
    const isEditOrCreate = params?.includes('edit') || params?.includes('create');
    return !!isEditOrCreate;
  }, [params]);

  // using same perms as campaigns
  const canCreateEditOrSendWorkflows = usePermissionCheck(
    [Permissions.CreateEditCampaigns, Permissions.SendCampaigns],
    'or'
  );

  // data
  const { data: campaigns = [], isLoading, isFetching, refetch } = useGetWorkflowCampaignsQuery();
  const { data: marketingTags = [] } = useGetMarketingTagsQuery();
  const { data: segments = [] } = useGetSegmentsQuery({ disableRefetch: true });

  const tagFilterOptions = useMemo(
    () => marketingTags.map((tag) => ({ id: tag.MarketingTagId, label: tag.TagName })),
    [marketingTags]
  );
  const segmentFilterOptions = useMemo(
    () => segments.map((segment) => ({ id: segment.SegmentId, label: segment.Name })),
    [segments]
  );

  // computed
  const columns = useColumns();
  const bulkActions = useBulkActions();
  const { selectedItems } = useMarketingCampaignsSharedActionModals();

  const rows = useMemo(
    () =>
      campaigns.filter((campaign) => {
        if (campaign.CampaignStatusId === CampaignStatus.ARCHIVED) {
          return false;
        }
        let matchesSearchFilter = true;
        let matchesStatusFilter = true;
        let matchesTagFilter = true;
        let matchesSegmentFilter = true;
        let matchesChannelFilter = true;

        if (searchFilter.length) {
          matchesSearchFilter = campaign.CampaignName.toLowerCase().includes(searchFilter.toLowerCase());
        }

        if (statusFilter.length) {
          matchesStatusFilter = statusFilter.map(({ id }) => id).includes(campaign.CampaignStatusId);
        }

        if (tagFilter.length) {
          const selectedTagIds = tagFilter.map(({ id }) => Number(id));

          const campaignTagIds = campaign.Tags?.map((tag) => tag.MarketingTagId) || [];
          matchesTagFilter = selectedTagIds.some((selectedId) => campaignTagIds.includes(selectedId));
        }

        if (channelFilter.length) {
          const selectedChannels = channelFilter.map(({ id }) => id);
          matchesChannelFilter = selectedChannels.some((channelId) => {
            if (channelId === 'email' && campaign.EmailConfig?.some((config) => config.IsActive)) {
              return true;
            }
            if (channelId === 'sms' && campaign.SMSConfig?.some((config) => config.IsActive)) {
              return true;
            }
            if (channelId === 'push' && campaign.PushConfig?.some((config) => config.IsActive)) {
              return true;
            }
            return false;
          });
        }

        if (segmentFilter.length) {
          const selectedSegmentIds = segmentFilter.map(({ id }) => Number(id));
          const campaignSegmentIds = campaign.RecipientSegments?.map((segment) => segment.SegmentId) || [];
          matchesSegmentFilter = selectedSegmentIds.some((selectedId) => campaignSegmentIds.includes(selectedId));
        }

        // If date range is selected, only show campaigns with send dates that fall within the range
        if (dateRange[0] && dateRange[1]) {
          // Exclude campaigns without send dates
          if (!campaign.ScheduleSendForUtc) {
            return false;
          }

          if (campaign.CampaignStatusId === CampaignStatus.DRAFT) {
            return false;
          }

          const sendDate = parseISO(campaign.ScheduleSendForUtc);
          return (
            matchesSearchFilter &&
            matchesStatusFilter &&
            matchesChannelFilter &&
            matchesTagFilter &&
            matchesSegmentFilter &&
            (isAfter(sendDate, dateRange[0]) || isEqual(sendDate, dateRange[0])) &&
            (isBefore(sendDate, dateRange[1]) || isEqual(sendDate, dateRange[1]))
          );
        }

        return (
          matchesSearchFilter && matchesStatusFilter && matchesTagFilter && matchesSegmentFilter && matchesChannelFilter
        );
      }) ?? [],
    [searchFilter, statusFilter, tagFilter, segmentFilter, campaigns, channelFilter, dateRange]
  );

  const handleSearchFilterChange = (value: string) => {
    setSearchFilter(value);
  };

  const handleStatusFilterChange = (value: DropdownMenuOption[]) => {
    setStatusFilter(value);
  };

  const handleTagFilterChange = (value: DropdownMenuOption[]) => {
    setTagFilter(value);
  };

  const handleSegmentFilterChange = (value: DropdownMenuOption[]) => {
    setSegmentFilter(value);
  };

  const handleChannelFilterChange = (value: DropdownMenuOption[]) => {
    setChannelFilter(value);
  };

  const handleDateRangeChange = (newDateRange: NonEmptyDateRange<Date> | [null, null]) => {
    setDateRange(newDateRange);
  };

  const handleRowClick = ({ row }) => {
    // For automated campaigns
    if (row.CampaignStatusId === CampaignStatus.DRAFT || row.CampaignStatusId === AutomatedCampaignStatus.INACTIVE) {
      navigate(`${WORKFLOW_WIZARD_PATHS.EDIT_WORKFLOW}/${String(row.MarketingCampaignId)}`);
    } else {
      navigate(`./overview/${String(row.MarketingCampaignId)}`);
    }
  };

  const handleDrawerClose = () => {
    navigate('/marketing/workflows');

    return null;
  };

  return (
    <>
      <Page variant={PageVariant.Table}>
        <HeaderContainer>
          <Heading>Workflows</Heading>
          <HeaderContainerButtonsWrapper>
            <Button
              label='Create workflow'
              variant='primary'
              onClick={() => navigate(`${WORKFLOW_WIZARD_PATHS.CREATE_WORKFLOW}`)}
            />
          </HeaderContainerButtonsWrapper>
        </HeaderContainer>

        <ControlBar>
          <ControlGroup grow isResponsive>
            <ControlSearchBox
              initialValue={searchFilter}
              placeholder='Search workflows...'
              onChange={handleSearchFilterChange}
            />
            <MultiFilterSelect
              activeFilter={statusFilter}
              label='Status'
              options={statusFilterOptions}
              onChange={handleStatusFilterChange}
            />
            <>
              <MultiFilterSelect
                activeFilter={channelFilter}
                label='Channel'
                options={channelFilterOptions}
                onChange={handleChannelFilterChange}
              />
              <MultiFilterSelect
                activeFilter={tagFilter}
                label='Tags'
                options={tagFilterOptions}
                onChange={handleTagFilterChange}
              />
              <MultiFilterSelect
                activeFilter={segmentFilter}
                label='Segments'
                options={segmentFilterOptions}
                onChange={handleSegmentFilterChange}
              />
              <DateRangePicker
                buttonLabel='Send date'
                value={dateRange[0] && dateRange[1] ? (dateRange as NonEmptyDateRange<Date>) : undefined}
                onDateChange={handleDateRangeChange}
              />
            </>

            <ControlSpacer />
            <RefreshButton isFetching={isFetching} onClick={refetch} />
          </ControlGroup>
        </ControlBar>
        <FixedHeightDataGrid
          bulkActionButtonAutomationId='workflows-bulk-actions-button'
          bulkActions={bulkActions}
          checkboxSelection
          columns={columns}
          disableSelectionOnClick
          getRowId={(row) => row.MarketingCampaignId}
          height='100%'
          loading={isLoading}
          name='workflows-list'
          rows={rows}
          onRowClick={handleRowClick}
        />
      </Page>
      {/* TODO: there is a bug in the reset behavior of the Campaign wizard's internal state. The simplest immediate fix is to stop rendering the wizard at all if it is closed (i.e. the expression below). This has the unintended side effect of breaking the drawer's entry animation. To restore the animation, we need to render unconditionally and pass isOpen={drawerOpen} here, but we'll also need to fix the wizard such that saving/closing results in all its internal state being reset */}
      {canCreateEditOrSendWorkflows && drawerOpen && <WorkflowCampaignBuilder isOpen onClose={handleDrawerClose} />}
      <SharedActionModals selectedItems={selectedItems} />
    </>
  );
}
